import React, { useEffect, useState } from "react";

const DISMISS_KEY = "label_install_dismissed_at";
const DISMISS_DAYS = 14; // 닫으면 14일간 다시 안 보여줌

function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true // iOS Safari
  );
}

function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

function isAndroid() {
  return /android/i.test(window.navigator.userAgent);
}

// 카카오톡 인앱 브라우저 감지 (UA에 KAKAOTALK 포함)
function isKakaoInApp() {
  return /kakaotalk/i.test(window.navigator.userAgent);
}

// 기타 인앱 브라우저(네이버/인스타/페북 등) — 카톡과 동일하게 취급
function isOtherInApp() {
  const ua = window.navigator.userAgent;
  return /naver|instagram|fban|fbav|line\//i.test(ua);
}

function isInAppBrowser() {
  return isKakaoInApp() || isOtherInApp();
}

function wasRecentlyDismissed() {
  const raw = localStorage.getItem(DISMISS_KEY);
  if (!raw) return false;
  const diffDays = (Date.now() - Number(raw)) / (1000 * 60 * 60 * 24);
  return diffDays < DISMISS_DAYS;
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [guideMode, setGuideMode] = useState(null); // null | 'ios' | 'inapp-ios' | 'inapp-android'

  const inApp = isInAppBrowser();
  const ios = isIOS();
  const android = isAndroid();

  useEffect(() => {
    if (isStandalone() || wasRecentlyDismissed()) return;

    // Android / Chrome / Edge 등: 브라우저가 설치 가능하다고 알려줌
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    // iOS Safari + 모든 인앱 브라우저는 beforeinstallprompt가 안 옴 → 직접 노출
    if (isIOS() || isInAppBrowser()) {
      setVisible(true);
    }

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
    };
  }, []);

  // 상황별 배너 문구
  const getBannerText = () => {
    if (inApp) {
      return {
        title: "LABEL 앱으로 더 빠르게",
        sub: ios
          ? "Safari에서 열면 홈 화면에 추가할 수 있어요"
          : "브라우저에서 열면 홈 화면에 추가할 수 있어요",
        btn: "방법 보기",
      };
    }
    return {
      title: "LABEL 앱으로 더 빠르게",
      sub: "홈 화면에 추가하고 앱처럼 사용해보세요",
      btn: "추가하기",
    };
  };

  const handleInstallClick = async () => {
    // 1) 인앱 브라우저(카톡 등)
    if (inApp) {
      if (android) {
        // 안드로이드: 크롬으로 강제 전환 시도 (intent 스킴)
        const url = window.location.href.replace(/^https?:\/\//, "");
        window.location.href = `intent://${url}#Intent;scheme=https;package=com.android.chrome;end`;
        // 전환 실패 대비 안내도 함께 표시
        setTimeout(() => setGuideMode("inapp-android"), 800);
      } else {
        // iOS: 자동 전환 불가 → 안내만
        setGuideMode("inapp-ios");
      }
      return;
    }

    // 2) iOS Safari
    if (ios) {
      setGuideMode("ios");
      return;
    }

    // 3) 안드로이드 크롬 등 정식 설치 지원
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setVisible(false);
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
    setGuideMode(null);
  };

  const handleCloseGuide = () => {
    setGuideMode(null);
  };

  if (!visible) return null;

  const text = getBannerText();

  // 안내 시트 내용 (상황별)
  const renderGuide = () => {
    if (guideMode === "inapp-ios") {
      return (
        <>
          <p style={styles.guideTitle}>홈 화면에 추가하기</p>
          <p style={styles.guideNote}>
            카카오톡에서는 바로 추가할 수 없어요. Safari로 옮긴 뒤 추가해주세요.
          </p>
          <ol style={styles.guideList}>
            <li>
              화면 아래 공유 버튼 <span style={styles.icon}>⬆︎</span> 탭
            </li>
            <li>
              <strong>"Safari로 열기"</strong> 선택
            </li>
            <li>
              Safari에서 다시 공유 <span style={styles.icon}>⬆︎</span> 탭
            </li>
            <li>
              <strong>"홈 화면에 추가"</strong> → 오른쪽 위 <strong>"추가"</strong>
            </li>
          </ol>
        </>
      );
    }

    if (guideMode === "inapp-android") {
      return (
        <>
          <p style={styles.guideTitle}>홈 화면에 추가하기</p>
          <p style={styles.guideNote}>
            크롬이 자동으로 열리지 않았다면 아래대로 해주세요.
          </p>
          <ol style={styles.guideList}>
            <li>
              오른쪽 위 <span style={styles.icon}>⋮</span> 또는 메뉴 버튼 탭
            </li>
            <li>
              <strong>"다른 브라우저로 열기"</strong> 선택
            </li>
            <li>
              크롬에서 메뉴 <span style={styles.icon}>⋮</span> →{" "}
              <strong>"홈 화면에 추가"</strong>
            </li>
          </ol>
        </>
      );
    }

    // guideMode === 'ios' (Safari)
    return (
      <>
        <p style={styles.guideTitle}>홈 화면에 추가하기</p>
        <ol style={styles.guideList}>
          <li>
            화면 아래 공유 버튼 <span style={styles.icon}>⬆︎</span> 탭
          </li>
          <li>
            <strong>"홈 화면에 추가"</strong> 선택
          </li>
          <li>
            오른쪽 위 <strong>"추가"</strong> 탭
          </li>
        </ol>
      </>
    );
  };

  return (
    <div style={styles.wrapper}>
      <div style={styles.banner}>
        <div style={styles.text}>
          <strong>{text.title}</strong>
          <div style={styles.subtext}>{text.sub}</div>
        </div>
        <div style={styles.actions}>
          <button style={styles.installBtn} onClick={handleInstallClick}>
            {text.btn}
          </button>
          <button
            style={styles.closeBtn}
            onClick={handleDismiss}
            aria-label="닫기"
          >
            ✕
          </button>
        </div>
      </div>

      {guideMode && (
        <div style={styles.guideOverlay} onClick={handleCloseGuide}>
          <div style={styles.guideCard} onClick={(e) => e.stopPropagation()}>
            {renderGuide()}
            <button style={styles.guideClose} onClick={handleCloseGuide}>
              확인
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  wrapper: {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1000,
  },
  banner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    padding: "12px 16px",
    paddingBottom: "calc(12px + env(safe-area-inset-bottom))",
    background: "#111111",
    color: "#ffffff",
    boxShadow: "0 -2px 12px rgba(0,0,0,0.15)",
  },
  text: { flex: 1, minWidth: 0 },
  subtext: { fontSize: "12px", color: "#bbbbbb", marginTop: "2px" },
  actions: { display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 },
  installBtn: {
    background: "#ffffff",
    color: "#111111",
    border: "none",
    borderRadius: "8px",
    padding: "8px 14px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  closeBtn: {
    background: "transparent",
    color: "#999999",
    border: "none",
    fontSize: "16px",
    cursor: "pointer",
    padding: "4px",
  },
  guideOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "flex-end",
    zIndex: 1001,
  },
  guideCard: {
    width: "100%",
    background: "#ffffff",
    borderRadius: "16px 16px 0 0",
    padding: "20px",
    paddingBottom: "calc(20px + env(safe-area-inset-bottom))",
  },
  guideTitle: {
    fontWeight: 700,
    fontSize: "16px",
    marginBottom: "8px",
    color: "#111",
  },
  guideNote: {
    fontSize: "13px",
    color: "#666",
    marginBottom: "14px",
    lineHeight: 1.5,
  },
  guideList: {
    paddingLeft: "20px",
    fontSize: "14px",
    lineHeight: 2,
    color: "#333",
    margin: 0,
  },
  icon: {
    display: "inline-block",
    padding: "0 4px",
    fontWeight: 700,
  },
  guideClose: {
    marginTop: "18px",
    width: "100%",
    padding: "13px",
    background: "#111111",
    color: "#ffffff",
    border: "none",
    borderRadius: "8px",
    fontWeight: 600,
    fontSize: "15px",
    cursor: "pointer",
  },
};
